import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { NeuButton, NeuInput, NeuSelect } from "../../components/neu/Neu";
import { ItemPicker, lineFromItem } from "./ItemPicker";
import { PriceBadge, usePriceCheck } from "./PriceCheck";
import { fmtMoney } from "../../lib/format";

export const EMPTY_LINE = { master_item_id: null, description: "", quantity: "1",
                            uom: "pcs", unit_price: "0.00", is_service: false,
                            tax_code_key: "ae_standard" };

const UOMS = ["pcs", "kg", "ton", "liter", "m", "m2", "m3", "bag",
              "box", "roll", "set", "hour", "day", "trip"];

const lineTotal = (line) => {
  const q = parseFloat(line.quantity || 0);
  const p = parseFloat(line.unit_price || 0);
  return isFinite(q * p) ? fmtMoney(q * p) : "0.00";
};

// سطر أمر شراء: بحث المادة + شارة الماركة + الكمية/الوحدة/السعر/الإجمالي الحي.
export const PurchaseLineEditor = ({ line, index, onChange, onRemove,
                                     currency = "AED", requireItem = true }) => {
  const { t, i18n } = useTranslation();
  const set = (key) => (e) => onChange(index, { ...line, [key]: e.target.value });
  const verdict = usePriceCheck(line.master_item_id, line.unit_price);
  const needsReason = verdict?.needs_reason;
  const picked = !!line.master_item_id;
  const label = picked ? ((i18n.language === "ar" ? line.description_ar : null) || line.description) : "";
  return (
    <div className="grid gap-2 rounded-2xl p-3 shadow-neu-in" data-testid={`purchase-line-${index}`}>
      <div className="flex items-center gap-3">
        <span className="num rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
          #{index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <ItemPicker testid={`purchase-line-item-${index}`}
                      onPick={(row) => onChange(index, { ...line, ...lineFromItem(row) })} />
        </div>
        <NeuButton className="px-2 py-2" data-testid={`purchase-line-remove-${index}`}
                   onClick={() => onRemove(index)}>
          <Trash2 className="h-3.5 w-3.5" />
        </NeuButton>
      </div>
      {picked ? (
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="rounded-lg bg-amber-50 px-2 py-0.5 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100">
            {t("purchases.brand")}: <b>{line.brand || t("purchases.noBrand")}</b>
          </span>
          <span className="font-bold">{label}</span>
        </div>
      ) : requireItem ? (
        <span className="text-[11px] text-amber-600">{t("purchases.items.pickFromMaster")}</span>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-4">
        <label className="grid gap-1">
          <span className="text-[10px] text-muted-foreground">{t("purchases.qty")}</span>
          <NeuInput data-testid={`purchase-line-qty-${index}`} dir="ltr"
                    value={line.quantity} onChange={set("quantity")} />
        </label>
        <label className="grid gap-1">
          <span className="text-[10px] text-muted-foreground">{t("purchases.unit")}</span>
          {picked ? (
            <div dir="ltr" data-testid={`purchase-line-uom-${index}`}
                 className="flex h-12 items-center rounded-2xl bg-muted/40 px-4 text-sm font-bold text-muted-foreground shadow-neu-in">
              {t(`purchases.units.${line.uom}`, { defaultValue: line.uom })}
            </div>
          ) : (
            <NeuSelect data-testid={`purchase-line-uom-${index}`}
                       value={line.uom} onChange={set("uom")}>
              {UOMS.map((u) => <option key={u} value={u}>{t(`purchases.units.${u}`)}</option>)}
            </NeuSelect>
          )}
        </label>
        <label className="grid gap-1">
          <span className="text-[10px] text-muted-foreground">{t("purchases.unitPrice")}</span>
          <NeuInput data-testid={`purchase-line-price-${index}`} dir="ltr"
                    value={line.unit_price} onChange={set("unit_price")} />
        </label>
        <label className="grid gap-1">
          <span className="text-[10px] text-muted-foreground">{t("purchases.lineTotal")}</span>
          <div dir="ltr" data-testid={`purchase-line-total-${index}`}
               className="num flex h-12 items-center rounded-2xl bg-primary/5 px-3 text-sm font-bold shadow-neu-in">
            {currency} {lineTotal(line)}
          </div>
        </label>
      </div>
      <PriceBadge verdict={verdict} testid={`purchase-line-price-badge-${index}`} />
      {needsReason ? (
        <NeuInput data-testid={`purchase-line-variance-${index}`}
                  placeholder={`${t("purchases.varianceReason")} *`}
                  value={line.price_variance_reason || ""} onChange={set("price_variance_reason")} />
      ) : null}
    </div>
  );
};
